import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Trade, Transaction, Wallet } from "../../types";
import { tradeService } from "../../services/trades";

interface TradeState {
  openTrades: Trade[];
  tradeHistory: Trade[];
  transactions: Transaction[];
  wallets: { real: Wallet | null; demo: Wallet | null };
  loading: boolean;
  error: string | null;
}

const initialState: TradeState = {
  openTrades: [],
  tradeHistory: [],
  transactions: [],
  wallets: { real: null, demo: null },
  loading: false,
  error: null,
};

// Explicitly fetch only open trades — never pollutes with closed
export const fetchOpenTrades = createAsyncThunk(
  "trade/fetchOpenTrades",
  async () => {
    const res = await tradeService.listTrades({
      status: "open",
      per_page: 100,
    });
    return res.data;
  },
);

// General fetch — used for paginated history etc
export const fetchTrades = createAsyncThunk(
  "trade/fetchTrades",
  async (params?: object) => {
    const res = await tradeService.listTrades(params);
    return res.data;
  },
);

export const fetchWallets = createAsyncThunk("trade/fetchWallets", async () => {
  const res = await tradeService.getWallets();
  return res.data;
});

export const fetchTransactions = createAsyncThunk(
  "trade/fetchTransactions",
  async (params?: object) => {
    const res = await tradeService.getTransactions(params);
    return res.data;
  },
);

const tradeSlice = createSlice({
  name: "trade",
  initialState,
  reducers: {
    clearError: (s) => {
      s.error = null;
    },
    // FIX: allow optimistic removal of a closed trade from openTrades
    // so the UI updates instantly without waiting for a re-fetch.
    removeTrade: (s, action: { payload: number }) => {
      s.openTrades = s.openTrades.filter((t) => t.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOpenTrades.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchOpenTrades.fulfilled, (s, a) => {
        s.loading = false;
        // Guard: only store trades that are actually open.
        // The backend already filters by status=open, but this is a
        // defensive client-side check in case a stale trade slips through.
        s.openTrades = (a.payload.data || []).filter(
          (t: Trade) => t.status === "open",
        );
      })
      .addCase(fetchOpenTrades.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message ?? "Failed to fetch open trades";
      })
      .addCase(fetchTrades.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchTrades.fulfilled, (s, a) => {
        s.loading = false;
        const all: Trade[] = a.payload.data || [];
        const open = all.filter((t) => t.status === "open");
        if (open.length > 0) s.openTrades = open;
        s.tradeHistory = all.filter((t) => t.status !== "open");
      })
      .addCase(fetchTrades.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message ?? "Failed to fetch trades";
      })
      .addCase(fetchWallets.fulfilled, (s, a) => {
        s.wallets.real = a.payload.real_wallet;
        s.wallets.demo = a.payload.demo_wallet;
      })
      .addCase(fetchTransactions.fulfilled, (s, a) => {
        s.transactions = a.payload.data || [];
      });
  },
});

export const { clearError, removeTrade } = tradeSlice.actions;
export default tradeSlice.reducer;
